import './styles/tailwind.css';

import {
  NavBarWrapper,
  RouteNotFound,
  TermsModal,
  VendorBooth,
} from '@app/components';
import { PostLoginRedirect } from '@app/components/post-login-redirect';
import { ToastContainer } from '@app/components/toast-messages/toast-container';
import { FLAG_SPACES } from '@app/feature-flags/flags';
import { FLAG_ENABLE_CONTENT_LIBRARY_ORG_DASHBOARD } from '@app/features/analytics/constants';
import { useCurrentUserQuery, useEventExistsQuery } from '@app/graphql';
import { useFlags } from '@app/hooks/use-flags';
import { AttendeeHomeRoute } from '@app/routes/attendee';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import {
  FLAG_CHANGE_CONTENT_LIBRARY_DEFAULT_TAB,
  FLAG_ENABLE_RECORDING_PAGE,
} from '@app/utilities/flags';
import { datadogLogs } from '@datadog/browser-logs';
import {
  GenericError,
  GenericErrorBoundary,
} from '@hopin-team/component-generic-error';
import {
  getDashboardBaseUrl,
  getLogoutUrl,
} from '@hopin-team/dashboard-constants';
import { RestrictContent } from '@hopin-team/dashboard-restrict-content';
import {
  constructURLWithParams,
  DashboardReactiveMapContext,
  DashboardReactiveMapData,
  getOrganizationIdFromPath,
} from '@hopin-team/dashboard-shared';
import {
  DashboardAnalyticsProvider,
  DashboardReactiveMapProvider,
  GraphqlProvider,
  useDashboardReactiveMap,
  useReadDashboardSharedState,
  useWriteDashboardSharedState,
} from '@hopin-team/dashboard-shared-react';
import { ThemeProvider } from '@hopin-team/provider-theme';
import {
  TranslationProvider,
  useTranslation,
} from '@hopin-team/provider-translation';
import { ReactIDProvider } from '@hopin-team/react-id';
import { getFromTheme } from '@hopin-team/ui-helper-deep-branding';
import { Loading as LoadingUI } from '@hopin-team/ui-loading';
import type { AppProps } from '@slicesjs/react-router-app';
import { QueryClientProvider } from '@tanstack/react-query';
import React, {
  ErrorInfo,
  lazy,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';

import { RingCentralChatWidget } from './components/rcx-chat-widget';
import { Analytics } from './features/analytics';
import { AnalyticsIframe } from './features/analytics-iframe';
import { TrackingConsentProvider } from './providers/tracking-consent-provider';
import { Apps } from './routes/apps';
import { AttendeeProfilePage } from './routes/attendee/routes/profile';
import { ConnectionsDirectoryPage } from './routes/connections';
import { Library } from './routes/content/routes/library';
import { Pages } from './routes/content/routes/pages';
import { Editor } from './routes/content/routes/recordings/routes/editor';
import { EditViewRoute } from './routes/content/routes/recordings/routes/editor/edit-view-route';
import { Lab } from './routes/content/routes/recordings/routes/lab';
import { RecordingPage as RecordingsRedirectPage } from './routes/content/routes/recordings/view/recording-page';
import {
  NavigateWithSearchParams,
  ProtectedWizardRoutes,
} from './routes/create-event-wizard/components/protected-routes';
import { Description } from './routes/create-event-wizard/routes/description';
import { EventAreas } from './routes/create-event-wizard/routes/event-areas';
import { ExperienceDetails } from './routes/create-event-wizard/routes/experience-details';
import { CreateOrganizationPage } from './routes/create-organization';
import { OrganizationEventsRoute } from './routes/organization/routes/events';
import { OrganizationProfilePage } from './routes/organization/routes/manage';
import { BillingPage } from './routes/organization/routes/manage/routes/billing';
import { SpacesPage } from './routes/organization/routes/manage/routes/spaces';
import { ActiveMemberRoute } from './routes/organization/routes/manage/routes/team/routes/active-member';
import { PendingInvitesRoute } from './routes/organization/routes/manage/routes/team/routes/pending-invites';
import { EventTeamAccessRoute } from './routes/organization/routes/manage-event';
import { OrganizationSeriesRoute } from './routes/organization/routes/series';
import {
  selectInitialSpaceIdResolved,
  selectOrganizationPaymentPlan,
  selectOrganizationStatus,
} from './routes/organization/selectors';
import { queryClient } from './services/query-client';
import { useInitialSpaceResolution } from './spaces/use-initial-space-resolution';
import { getUserExternalId } from './store/selectors';
import { StoreProvider } from './store/store-provider';

/* Lazy loading the modals as they may not be needed for first load. */
const ManageOrganizationModal = lazy(
  () => import('@app/components/manage-organization-modal'),
);
const CreateOrganizationModal = lazy(
  () => import('@app/components/create-organization-modal'),
);
const ManageProfileModal = lazy(
  () => import('@app/components/manage-profile-modal'),
);
const EventTeamAccessModal = lazy(
  () => import('@app/components/event-team-access-modal'),
);

const GlobalStyle = createGlobalStyle`
  html, body {
    height: 100%;
    margin: 0;
    background-color: ${() => getFromTheme(`COLOR.GREY['WHITE']`)};
  }

  // this div is the slice root container so can't style using styled.
  body > div:first-child {
    min-height: 100vh;
  }

  .dimelo-chat-bubble,
  .dimelo-chat-container,
  .dimelo_chat_button {
    left: 0.5rem !important;
    right: auto !important;
  }
`;

/*
  AppContainer wraps the entire application - NavBar and content.
  It stretches to 100% of the vh and displays flex so the content area
  can grow vertically (the NavBar is fixed height)
*/
const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  .hpn-Butticon .hpn-Icon {
    flex-direction: column;
  }
`;

/*
  ContentContainer grows vertically to take up the rest of the screen left by the NavBar.
  There's a div being rendered somewhere - think as a Slices root component - so we need
  to extend that one as well.
*/
const ContentContainer = styled.div`
  flex-grow: 1;
  display: flex;
  & > div {
    flex-grow: 1;
  }
`;

const Loading = () => {
  const { t } = useTranslation();
  return <LoadingUI text={t('loading')} poweredByText={t('poweredBy')} />;
};

const OrganizationWrapper = ({
  children,
}: {
  children: ReactElement<unknown, string>;
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const reactiveMap = useDashboardReactiveMap();
  const organizationId = getOrganizationIdFromPath(location.pathname);
  const userId = useAppSelector(getUserExternalId);
  const organizationPaymentPlan = useAppSelector(selectOrganizationPaymentPlan);
  const organizationDetailsFetchingStatus = useAppSelector(
    selectOrganizationStatus,
  );

  /*
   this query just fetches the organization id (to check if it exists) and takes
   almost one fourth of the time of the entire organization details fetch.
   waiting for organization details fetch to complete is not a good idea as it
   will block the entire organization dashboard from loading for a long time.

   Space resolution (initial spaceid resolved dispatch) is handled by
   useInitialSpaceResolution, which also fixes the resolution precedence for
   direct editor landings carrying a space_external_id URL parameter.
   */
  // skipWhenResolved: false — OrganizationWrapper also uses data.organization
  // as the org-existence guard below, so the query must always run regardless
  // of whether the initial space has already been resolved.
  const { data, error, fetching } = useInitialSpaceResolution({
    organizationId: organizationId || '',
    userId: userId || '',
    skipWhenResolved: false,
  });

  useEffect(() => {
    if (!organizationId || organizationDetailsFetchingStatus !== 'success')
      return;

    reactiveMap.getMapContext('launchdarkly_client').then(client => {
      if (!client) return;

      const currentContext = client.getContext();

      client.identify({
        ...currentContext,
        organizationExternalId: organizationId,
        custom: {
          ...currentContext.custom,
          organizationExternalId: organizationId,
          organizationPaymentPlan: organizationPaymentPlan?.planName,
        },
      });
    });
  }, [
    reactiveMap,
    organizationPaymentPlan,
    organizationDetailsFetchingStatus,
    organizationId,
  ]);

  const [knownOrganizationId, setKnownOrganizationId] = useState<string>();

  useEffect(() => {
    if (!fetching && !error && data?.organization && organizationId) {
      setKnownOrganizationId(organizationId);
    }
  }, [fetching, error, data, organizationId]);

  // The query runs again when the selected Space changes. Once this org is
  // known to exist, keep the page mounted instead of blanking it meanwhile.
  const isOrganizationKnown =
    !!organizationId && knownOrganizationId === organizationId;

  if (error) {
    return <GenericError error={error.message} />;
  } else if ((fetching && !isOrganizationKnown) || !userId || !organizationId) {
    return null;
  } else if (!fetching && !data?.organization) {
    return (
      <GenericError
        pt={4}
        tryAgainText={t('orgDashboard.organization.errorInvalidUrlRetry')}
        errorHeading={t('orgDashboard.organization.errorInvalidUrlHeading')}
        tryAgainAction={() => navigate('/')}
        errorBody=""
      />
    );
  }
  return children;
};

const EventWrapper = ({
  children,
}: {
  children: ReactElement<unknown, string>;
}) => {
  const { t } = useTranslation();
  const updateEventId = useWriteDashboardSharedState('event_id');
  const navigate = useNavigate();
  const { eventSlug } = useParams();
  const dispatch = useAppDispatch();
  const [{ data, error, fetching }] = useEventExistsQuery({
    variables: {
      eventSlug: eventSlug || '',
    },
    pause: !eventSlug,
  });

  useEffect(() => {
    if (data?.eventBySlug) {
      updateEventId(data.eventBySlug.id);
      dispatch({
        type: '[event-team-access] update event id',
        payload: { eventId: data.eventBySlug.id },
      } as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (error) {
    return <GenericError error={error.message} />;
  } else if (fetching) {
    return null;
  } else if (!eventSlug || (!fetching && !data?.eventBySlug)) {
    return (
      <GenericError
        pt={4}
        tryAgainText={t('orgDashboard.event.errorInvalidUrlRetry')}
        errorHeading={t('orgDashboard.event.errorInvalidUrlHeading')}
        tryAgainAction={() => navigate('/')}
        errorBody=""
      />
    );
  }
  return children;
};

/**
 * SpaceContextGate — mounted on the standalone recordings editor route branch
 * (outside OrganizationWrapper).  It resolves the active Space before children
 * render so that RecordingEffect never races against an unresolved space when
 * the user lands directly on a /editor URL.
 *
 * A `space_external_id` query parameter takes highest precedence and is used
 * to synchronize the active Space to the recording's owning Space.
 */
const SpaceContextGate = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const organizationId = getOrganizationIdFromPath(location.pathname);
  const userId = useAppSelector(getUserExternalId);
  const isInitialSpaceIdResolved = useAppSelector(selectInitialSpaceIdResolved);

  const candidateSpaceExternalId =
    new URLSearchParams(location.search).get('space_external_id') ?? undefined;

  // skipWhenResolved: false — we need data.organization for the invalid-org
  // guard below, and the query must continue running after the initial space
  // resolution so that a direct landing on a non-existent org slug is caught.
  const { data, error, fetching } = useInitialSpaceResolution({
    organizationId: organizationId || '',
    userId: userId || '',
    candidateSpaceExternalId,
    skipWhenResolved: false,
  });

  if (error) {
    return <GenericError error={error.message} />;
  }

  if (!fetching && !userId && !organizationId) {
    return null;
  }

  // If the query has settled with no organization, the org slug in the URL is
  // invalid — show the same invalid-org error as OrganizationWrapper.
  if (!fetching && userId && organizationId && !data?.organization) {
    return (
      <GenericError
        pt={4}
        tryAgainText={t('orgDashboard.organization.errorInvalidUrlRetry')}
        errorHeading={t('orgDashboard.organization.errorInvalidUrlHeading')}
        tryAgainAction={() => navigate('/')}
        errorBody=""
      />
    );
  }

  // Block rendering until space is resolved so RecordingEffect's
  // waitForInitialSpaceIdResolved can proceed before content-hub auth.
  if (!isInitialSpaceIdResolved && (fetching || !userId || !organizationId)) {
    return null;
  }

  return <>{children}</>;
};

const StoreUser = ({ children }: { children: ReactNode }) => {
  const updateUser = useWriteDashboardSharedState('user');

  const [{ data, error, fetching }] = useCurrentUserQuery();

  useEffect(() => {
    if (!error && data?.me?.id && data?.me?.email) {
      updateUser({
        id: data.me.id,
        email: data.me.email,
      });
    }
  }, [data, error, updateUser]);

  if (error) {
    // we don't want to redirect to the login url in case of GQL error
    // if the user is not logged in, we redirect to the login url before hitting this endpoint
    // so we are sure that the error here is a different one
    return <GenericError error={error.message} />;
  }

  if (!fetching && !data?.me) {
    return (
      <GenericError
        tryAgainText="Sign in"
        tryAgainAction={() => {
          const currentPath = new URL(location.pathname, getDashboardBaseUrl());
          const loginPath = `/sign_in?redirect_url=${currentPath.toString()}`;
          location.assign(
            constructURLWithParams(getLogoutUrl(), {
              redirect_url: loginPath,
            }),
          );
        }}
      />
    );
  }

  return <>{children}</>;
};

const App = () => {
  const { t } = useTranslation();

  const flags = useFlags();
  const enableCHLibraryOrgDashboard =
    flags?.[FLAG_ENABLE_CONTENT_LIBRARY_ORG_DASHBOARD];

  const isContentLibraryDefaultTabChanged = Boolean(
    flags?.[FLAG_CHANGE_CONTENT_LIBRARY_DEFAULT_TAB],
  );
  const isSpacesEnabled = Boolean(flags?.[FLAG_SPACES]);

  return (
    <TrackingConsentProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <DashboardAnalyticsProvider fallback={<Loading />}>
            <StoreUser>
              <Routes>
                <Route path="/" element={<MainLayout />}>
                  <Route index element={<PostLoginRedirect />} />
                  <Route path="/attendee" element={<Outlet />}>
                    <Route index element={<AttendeeHomeRoute />} />
                    <Route
                      path="profile"
                      element={
                        <ManageProfileModal>
                          <AttendeeProfilePage />
                        </ManageProfileModal>
                      }
                    />
                  </Route>
                  <Route
                    path="connections/*"
                    element={<ConnectionsDirectoryPage />}
                  />
                  <Route path="/vendor/:boothId">
                    <Route index element={<VendorBooth />} />
                  </Route>
                  <Route
                    path="/org/new"
                    element={
                      <CreateOrganizationModal>
                        <CreateOrganizationPage />
                      </CreateOrganizationModal>
                    }
                  />
                  <Route
                    path="/org/:organizationSlug"
                    element={
                      <OrganizationWrapper>
                        <Outlet />
                      </OrganizationWrapper>
                    }
                  >
                    <Route element={<Outlet />}>
                      <Route index element={<Navigate to="events" replace />} />
                      <Route
                        path="events"
                        element={<OrganizationEventsRoute />}
                      />
                      <Route
                        path="series"
                        element={<OrganizationSeriesRoute />}
                      />
                    </Route>
                    <Route
                      path="apps"
                      element={
                        <RestrictContent
                          rules={[
                            {
                              roles: ['ADMIN'],
                            },
                          ]}
                          fallback={
                            <GenericError
                              errorHeading={t('notAuthorized.title')}
                              errorBody={t('notAuthorized.body')}
                            />
                          }
                        >
                          {<Apps />}
                        </RestrictContent>
                      }
                    />
                    <Route
                      path="content"
                      element={
                        <RestrictContent
                          rules={[
                            {
                              roles: ['ADMIN'],
                            },
                          ]}
                          fallback={
                            <GenericError
                              errorHeading={t('notAuthorized.title')}
                              errorBody={t('notAuthorized.body')}
                            />
                          }
                        >
                          <Outlet />
                        </RestrictContent>
                      }
                    >
                      {isContentLibraryDefaultTabChanged &&
                      enableCHLibraryOrgDashboard ? (
                        <Route
                          index
                          element={<Navigate to={'library'} replace />}
                        />
                      ) : (
                        <Route
                          index
                          element={<Navigate to={'hubs'} replace />}
                        />
                      )}
                      <Route path="hubs" element={<Pages />} />
                      {enableCHLibraryOrgDashboard && (
                        <Route path="library" element={<Library />} />
                      )}
                      <Route path="recordings">
                        <Route
                          index
                          element={<Navigate to="../../library" replace />}
                        />
                        <Route path="view">
                          <Route
                            index
                            element={<Navigate to="../../../library" replace />}
                          />
                          <Route
                            path=":recordingId"
                            element={<RecordingsRedirectPage />}
                          />
                        </Route>
                      </Route>
                    </Route>
                    <Route path="analytics" element={<Analytics />} />
                    <Route
                      path="event/:eventSlug/team-access"
                      element={
                        <RestrictContent
                          rules={[
                            {
                              roles: ['ADMIN'],
                              organizationFeatures: {
                                values: ['rolesAndPermissions'],
                              },
                            },
                          ]}
                          fallback={
                            <GenericError
                              errorHeading={t('notAuthorized.title')}
                              errorBody={t('notAuthorized.body')}
                            />
                          }
                        >
                          <EventWrapper>
                            <EventTeamAccessModal>
                              <EventTeamAccessRoute />
                            </EventTeamAccessModal>
                          </EventWrapper>
                        </RestrictContent>
                      }
                    />
                    <Route
                      path="manage"
                      element={
                        <RestrictContent
                          rules={[{ roles: ['ADMIN'] }]}
                          fallback={
                            <GenericError
                              errorHeading={t('notAuthorized.title')}
                              errorBody={t('notAuthorized.body')}
                            />
                          }
                        >
                          <ManageOrganizationModal>
                            <Outlet />
                          </ManageOrganizationModal>
                        </RestrictContent>
                      }
                    >
                      <Route index element={<OrganizationProfilePage />} />
                      <Route path="billing" element={<BillingPage />} />
                      {isSpacesEnabled && (
                        <Route path="spaces" element={<SpacesPage />} />
                      )}
                      <Route path="team" element={<Outlet />}>
                        <Route
                          index
                          element={<Navigate to="active-members" replace />}
                        />
                        <Route
                          path="active-members"
                          element={<ActiveMemberRoute />}
                        />
                        <Route
                          path="pending-invites"
                          element={<PendingInvitesRoute />}
                        />
                      </Route>
                    </Route>
                    <Route path="*" element={<RouteNotFound />} />
                  </Route>
                  <Route path="*" element={<RouteNotFound />} />
                </Route>
                <Route
                  path="/create-event-wizard"
                  element={<ProtectedWizardRoutes />}
                >
                  <Route
                    index
                    element={
                      <NavigateWithSearchParams
                        to="experience-details"
                        replace
                      />
                    }
                  />
                  <Route
                    path="experience-details"
                    element={<ExperienceDetails />}
                  />
                  <Route path="event-areas" element={<EventAreas />} />
                  <Route path="description" element={<Description />} />
                </Route>
                <Route
                  path="/org/:organizationSlug/create-event-wizard"
                  element={
                    <OrganizationWrapper>
                      <RestrictContent
                        rules={[
                          {
                            roles: ['ADMIN'],
                          },
                        ]}
                        fallback={
                          <GenericError
                            errorHeading={t('notAuthorized.title')}
                            errorBody={t('notAuthorized.body')}
                          />
                        }
                      >
                        <ProtectedWizardRoutes />
                      </RestrictContent>
                    </OrganizationWrapper>
                  }
                >
                  <Route
                    index
                    element={
                      <NavigateWithSearchParams
                        to="experience-details"
                        replace
                      />
                    }
                  />
                  <Route
                    path="experience-details"
                    element={<ExperienceDetails />}
                  />
                  <Route path="event-areas" element={<EventAreas />} />
                  <Route path="description" element={<Description />} />
                </Route>
                <Route
                  path="/org/:organizationSlug/analytics-iframe"
                  element={
                    <OrganizationWrapper>
                      <AnalyticsIframe />
                    </OrganizationWrapper>
                  }
                />
                <Route
                  path="/org/:organizationSlug/content/recordings"
                  element={
                    <SpaceContextGate>
                      <Outlet />
                    </SpaceContextGate>
                  }
                >
                  <Route
                    path=":recordingId"
                    element={
                      <RestrictContent
                        rules={[
                          {
                            roles: ['ADMIN', 'MEMBER'],
                            featureFlags: {
                              values: [FLAG_ENABLE_RECORDING_PAGE],
                              mode: 'AND',
                            },
                          },
                        ]}
                        fallback={
                          <GenericError
                            errorHeading={t('notAuthorized.title')}
                            errorBody={t('notAuthorized.body')}
                          />
                        }
                      >
                        <Outlet />
                      </RestrictContent>
                    }
                  >
                    <Route
                      index
                      element={<NavigateWithSearchParams to="editor" replace />}
                    />
                    <Route path="editor">
                      <Route index element={<Editor />} />
                      {/* EditViewRoute inherits RestrictContent auth guard from the parent :recordingId route */}
                      <Route path="edits/:editId" element={<EditViewRoute />} />
                    </Route>
                    <Route path="lab">
                      <Route index element={<Lab />} />
                      <Route path=":tabType" element={<Lab />} />
                    </Route>
                  </Route>
                </Route>
              </Routes>
            </StoreUser>
            <ToastContainer />
          </DashboardAnalyticsProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </TrackingConsentProvider>
  );
};

const DashboardApp = ({
  reactiveMap,
}: AppProps<DashboardReactiveMapData, DashboardReactiveMapContext>) => {
  return (
    <ReactIDProvider>
      <GenericErrorBoundary onError={reportToDatadog}>
        <DashboardReactiveMapProvider reactiveMap={reactiveMap}>
          <StoreProvider>
            <GlobalStyle />
            <TranslationProvider
              fallback={<div />} // Show blank screen while loading, since we can't translate the loading text
              reactiveMapLocale={{
                useReadSharedState: useReadDashboardSharedState,
                key: 'i18n_locale',
              }}
              getLocale={(locale: string) => import(`./locales/${locale}.json`)}
            >
              <GraphqlProvider fallback={<Loading />}>
                <App />
              </GraphqlProvider>
            </TranslationProvider>
          </StoreProvider>
        </DashboardReactiveMapProvider>
      </GenericErrorBoundary>
    </ReactIDProvider>
  );
};

const MainLayout = () => {
  const { pathname } = useLocation();

  if (pathname === '/') {
    return <Outlet />;
  }
  return (
    <AppContainer>
      <NavBarWrapper />
      <ContentContainer>
        <Outlet />
      </ContentContainer>
      <TermsModal />
      <RingCentralChatWidget />
    </AppContainer>
  );
};

const reportToDatadog = (error: Error, _errorInfo: ErrorInfo) => {
  datadogLogs.logger.error('React Error Boundary', {
    error: error.message,
    stack: error.stack,
    source: 'react-error-boundary',
    errorInfo: _errorInfo,
  });
};

// eslint-disable-next-line import/no-default-export
export default DashboardApp;
